import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  'https://backend-clinical-intake.dizzaroo.com'
).replace(/\/$/, '');

// Spoke backend starts OAuth (PKCE); it then redirects the browser to Hub
const SSO_LOGIN_URL = `${API_BASE}/api/auth/login`;

const HUB_FRONTEND_URL = (
  process.env.REACT_APP_HUB_FRONTEND_URL ||
  'https://hub-uat.dizzaroo.com'
).replace(/\/$/, '');

const OAUTH_ERROR_MESSAGES = {
  invalid_state: 'Sign-in session expired. Please try again.',
  missing_verifier: 'Sign-in session expired. Please try again.',
  token_exchange_failed: 'Could not complete Hub sign-in. Please try again.',
  verification_failed: 'Hub could not verify your account.',
  missing_code: 'Missing authorization code from Hub.',
  session_error: 'Could not start sign-in session. Please try again.',
  callback_failed: 'Sign-in failed. Please try again.',
  exchange_failed: 'Sign-in session expired. Please try again.',
};

/**
 * Silent OAuth landing page — no permanent "Sign in with Hub" UI.
 * - ?code=… → redeem the one-time code for the app JWT via POST /api/auth/exchange,
 *   then store it and enter the app (never show "Redirecting to Hub"). The JWT
 *   itself never appears in this URL — see security review SEC-012/SEC-004 on
 *   why a bearer token in a query string is a leak (history, Referer, access logs).
 * - ?error=… → show toast, then restart Hub SSO
 * - ?logged_out=1 → stay signed out (do NOT auto-restart SSO)
 * - otherwise → redirect straight to Hub via backend /api/auth/login
 */
const Login = () => {
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading: authLoading, consumePostLoginRedirect } = useAuth();
  const handled = useRef(false);
  const loggedOut = searchParams.get('logged_out') === '1';
  const code = searchParams.get('code');
  const error = searchParams.get('error');

  useEffect(() => {
    if (handled.current) return;

    // Returning from Hub with a one-time exchange code — redeem it for the
    // app JWT immediately (do not wait on empty-session check, and never
    // bounce back to Hub).
    if (code) {
      handled.current = true;
      fetch(`${API_BASE}/api/auth/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || !data?.token) throw new Error(data?.message || 'Exchange failed');
          login(data.token);
          toast.success('Successfully logged in!');
        })
        .catch((err) => {
          console.error('Code exchange failed:', err);
          toast.error(OAUTH_ERROR_MESSAGES.exchange_failed);
          window.location.replace(SSO_LOGIN_URL);
        });
      return;
    }

    // Wait for AuthContext to finish /auth/me before deciding to bounce to Hub
    if (authLoading) return;

    if (isAuthenticated) {
      handled.current = true;
      window.location.replace(consumePostLoginRedirect());
      return;
    }

    // After logout: do not auto-start SSO (that caused silent re-login in production)
    if (loggedOut) {
      handled.current = true;
      return;
    }

    if (error) {
      handled.current = true;
      toast.error(OAUTH_ERROR_MESSAGES[error] || 'Login failed. Please try again.');
      const t = setTimeout(() => {
        window.location.replace(SSO_LOGIN_URL);
      }, 1200);
      return () => clearTimeout(t);
    }

    handled.current = true;
    window.location.replace(SSO_LOGIN_URL);
  }, [searchParams, login, isAuthenticated, loggedOut, authLoading, code, error, consumePostLoginRedirect]);

  if (loggedOut) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
        }}
      >
        <Typography variant="h5">You have been signed out</Typography>
        <Typography color="text.secondary" align="center">
          Your Hub session was cleared. Sign in again when you are ready.
        </Typography>
        <Button variant="contained" onClick={() => { window.location.href = SSO_LOGIN_URL; }}>
          Sign in with Hub
        </Button>
        <Button variant="text" href={HUB_FRONTEND_URL}>
          Go to Hub
        </Button>
      </Box>
    );
  }

  // Message order matters: code return from Hub must never look like a Hub redirect.
  let statusMessage = 'Redirecting to Hub sign-in…';
  if (code) {
    statusMessage = 'Signing you in…';
  } else if (authLoading) {
    statusMessage = 'Checking your session…';
  } else if (isAuthenticated) {
    statusMessage = 'Opening the app…';
  } else if (error) {
    statusMessage = 'Sign-in failed. Retrying Hub sign-in…';
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">{statusMessage}</Typography>
    </Box>
  );
};

export default Login;
